// frontend/src/pages/admin/TicketTypeForm.js
import React, { useEffect, useState, useCallback } from 'react';
import { Modal, Form, Input, Button } from 'antd';
import api from '../../utils/api';
import { useNotificationApi } from '../../context/NotificationContext';

const TicketTypeForm = ({ visible, onCancel, onSuccess, ticketType }) => {
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false); // Retained original 'loading' state for buttons
  const notificationApi = useNotificationApi();

  // Helper functions for notifications (showError, showSuccess, showLoading) remain the same
  const showError = useCallback((title, content, key = 'globalError') => {
    notificationApi.destroy(key);
    try {
      notificationApi.error({
        key,
        message: title,
        description: content,
        duration: 4.5,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    } catch (err) {
      console.error("Notification failed, falling back to Modal.error:", err);
      Modal.error({
        title,
        content,
        centered: true,
        zIndex: 99999,
      });
    }
  }, [notificationApi]);

  const showSuccess = useCallback((title, description, key = 'globalSuccess') => {
    notificationApi.success({
      key,
      message: title,
      description: description,
      duration: 3,
      placement: 'topRight',
      style: {
        width: 400,
        zIndex: 10000,
      },
    });
  }, [notificationApi]);

  const showLoading = useCallback((messageContent, key = 'globalLoading') => {
    let dismissNotification;
    const delay = 300;
    const minDisplayTime = 500;

    const startTime = Date.now();

    const timer = setTimeout(() => {
      dismissNotification = notificationApi.open({
        key,
        message: messageContent,
        duration: 0,
        placement: 'topRight',
        style: {
          width: 400,
          zIndex: 10000,
        },
      });
    }, delay);

    return () => {
      clearTimeout(timer);
      const elapsed = Date.now() - startTime;
      const remaining = Math.max(0, minDisplayTime - elapsed);

      setTimeout(() => {
        if (dismissNotification) {
          typeof dismissNotification === 'function' ? dismissNotification() : notificationApi.destroy(key);
        } else {
          notificationApi.destroy(key);
        }
      }, remaining);
    };
  }, [notificationApi]);


  // Effect to populate form fields and reset loading state when editing/adding
  useEffect(() => {
    if (visible) {
      setLoading(false); // <--- ADD THIS LINE: Reset loading state when modal opens
      if (ticketType) {
        form.setFieldsValue({
          name: ticketType.name,
        });
      } else {
        form.resetFields();
      }
    } else {
      form.resetFields(); // Reset form fields when modal is closed
    }
  }, [visible, ticketType, form]);


  const handleSubmit = async () => {
    let hideLoading;
    try {
      setLoading(true);
      hideLoading = showLoading(ticketType ? 'Updating ticket type...' : 'Adding ticket type...', 'ticketTypeFormLoading');

      const values = await form.validateFields();

      const payload = {
        name: values.name,
      };

      if (ticketType) {
        console.log('TicketTypeForm: Attempting to update ticket type:', ticketType.id, payload);
        await api.put(`/api/ticket-types/${ticketType.id}`, payload);
        showSuccess('Ticket Type Updated', 'Ticket type details updated successfully!', 'ticketTypeUpdateSuccess');
      } else {
        console.log('TicketTypeForm: Attempting to create new ticket type:', payload);
        await api.post('/api/ticket-types', payload);
        showSuccess('Ticket Type Created', 'New ticket type created successfully!', 'ticketTypeCreateSuccess');
      }

      setTimeout(() => {
        if (hideLoading && typeof hideLoading === 'function') hideLoading();
        onSuccess(true);
        onCancel();
      }, 500);

    } catch (error) {
      if (hideLoading && typeof hideLoading === 'function') hideLoading();
      setLoading(false);
      console.error('TicketTypeForm: Form submission error:', error.response?.data || error.message, error);
      showError('Submission Failed', error.response?.data?.message || 'Failed to save ticket type.', 'ticketTypeFormSubmitError');
    } finally {
      // setLoading is handled in catch and setTimeout, so no need for a final setLoading(false) here.
    }
  };

  return (
    <Modal
      open={visible}
      title={ticketType ? 'Edit Ticket Type' : 'Add New Ticket Type'}
      onCancel={onCancel}
      footer={[
        <Button key="back" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>,
        <Button key="submit" type="primary" loading={loading} onClick={handleSubmit}>
          Submit
        </Button>,
      ]}
      width={600}
      destroyOnClose={true}
    >
      <Form
        form={form}
        layout="vertical"
        name="ticket_type_form"
      >
        <Form.Item
          name="name"
          label="Type Name"
          rules={[
            { required: true, message: 'Please input the type name!' },
            { type: 'string', min: 2, message: 'Type name must be at least 2 characters.' }
          ]}
        >
          <Input placeholder="e.g., Incident, Service Request" />
        </Form.Item>
      </Form>
    </Modal>
  );
};

export default TicketTypeForm;